import React, { useState, useEffect, useRef, useCallback, createContext, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import CommandPalette, { useCommandPalette } from './CommandPalette';
import { LeadRunNotice } from './leads/LeadRunNotice';
import { Menu } from 'lucide-react';
import QuickStartChecklist from './QuickStartChecklist';
import BetaEndedBanner from './BetaEndedBanner';
import LifetimeOfferModal, { useCountdown } from './LifetimeOfferModal';
import { useBetaEnd } from '../context/BetaEndContext';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Users,
  Link2,
  CreditCard,
  Settings,
  Shield,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Check,
  Plus,
  Search,
  Sun,
  Moon,
  HelpCircle,
  Command,
  X,
  Keyboard,
  Coins,
  Infinity,
  AlertTriangle,
  Crown,
  MessageCircle,
  GraduationCap,
  Handshake,
  ClipboardList,
  Bot,
  Radar,
  Flame,
  CheckCircle2,
  XCircle,
} from 'lucide-react';

const SidebarContext = createContext();

export const useSidebar = () => useContext(SidebarContext);

import VoxinityLogo from './VoxinityLogo';
import PaymentAlertBanner from './common/PaymentAlertBanner';
import CapacityUpgradeModal from './CapacityUpgradeModal';
import NotificationBell from './NotificationBell';
import ConcurrencyWatcher from './concurrency/ConcurrencyWatcher';

// Custom Logo Component with fallback.
// Expanded sidebar: rectangular logos fill the sidebar width (capped at 64px tall),
// square-ish logos render at 64px. Collapsed sidebar: compact 32px square box.
export const LogoImage = ({ logoData, businessName, collapsed = false }) => {
  const [hasError, setHasError] = useState(false);
  const [isSquare, setIsSquare] = useState(false);

  if (hasError || !logoData) {
    return (
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-phosphor-400 flex items-center justify-center flex-shrink-0">
        <span className="text-space-900 font-bold text-sm">{(businessName || 'V').charAt(0).toUpperCase()}</span>
      </div>
    );
  }

  if (collapsed) {
    return (
      <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
        <img 
          src={logoData} 
          alt={businessName || 'Logo'} 
          className="w-full h-full object-contain"
          onError={() => setHasError(true)}
          data-testid="whitelabel-logo-collapsed"
        />
      </div>
    );
  }

  return (
    <img 
      src={logoData} 
      alt={businessName || 'Logo'} 
      onLoad={(e) => setIsSquare(e.target.naturalWidth / e.target.naturalHeight < 1.4)}
      onError={() => setHasError(true)}
      className={`${isSquare ? 'h-16 w-auto' : 'w-full max-h-16'} object-contain`}
      data-testid="whitelabel-logo"
    />
  );
};

// Help Modal Component — Tips, Getting Started & Shortcuts
const HelpModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const tips = [
    {
      title: 'Create your first sub-account',
      body: 'Sub-accounts are where your agents, phone numbers, and knowledge bases live. Open Sub-Accounts in the sidebar to set one up.',
    },
    {
      title: 'Fund your workspace',
      body: 'Voxinity is pay-as-you-go. Go to Billing → Top Up Balance. Usage is pulled in real time as your agents run calls and chats.',
    },
    {
      title: 'Keep your own sub-account Unlimited',
      body: 'Open Billing → find your sub-account → Set Limit → Unlimited. This lets your personal agents draw from your agency balance without a cap.',
    },
    {
      title: 'Turn on Auto Top-off',
      body: 'Billing → Auto Top-off — set a threshold and a top-up amount so your balance refills itself before running low.',
    },
    {
      title: 'Re-billing for client sub-accounts',
      body: 'Billing → Sub-Account Re-billing. Turn ON to let each sub-account manage its own billing with your custom markup.',
    },
    {
      title: 'Check live call transcripts',
      body: 'Every call has a full transcript in the Conversations tab — great for QA and training your agents.',
    },
  ];

  const shortcuts = [
    { keys: ['Esc'], description: 'Close modal' },
    { keys: ['↑', '↓'], description: 'Navigate lists' },
    { keys: ['Enter'], description: 'Select item' },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[90] p-4 flex items-start justify-center overflow-y-auto"
        onClick={onClose}
        data-testid="help-modal"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full max-w-2xl mt-[6vh] mb-8"
          onClick={e => e.stopPropagation()}
        >
          <div className="bg-gradient-to-b from-[#131519] to-[#0a0b0f] border border-white/10 rounded-2xl shadow-[0_30px_60px_-10px_rgba(0,0,0,0.9)] overflow-hidden">
            <div className="p-6 border-b border-white/[0.06] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-teal-400/10 border border-teal-400/20 flex items-center justify-center">
                  <HelpCircle className="text-teal-400" size={22} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-white">Tips & Help</h2>
                  <p className="text-sm text-gray-400">Quick wins for getting the most out of Voxinity AI</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10" data-testid="help-modal-close">
                <X size={18} />
              </button>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3">Getting Started</h3>
              <div className="space-y-2.5 mb-6">
                {tips.map((t, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 hover:bg-white/[0.04] transition-colors"
                    data-testid={`help-tip-${i}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-lg bg-teal-400/15 text-teal-300 text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                        {i + 1}
                      </div>
                      <div>
                        <div className="text-[14px] font-semibold text-white">{t.title}</div>
                        <p className="text-[13px] text-gray-400 mt-1 leading-relaxed">{t.body}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3">Keyboard Shortcuts</h3>
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 space-y-2.5">
                {shortcuts.map((shortcut, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">{shortcut.description}</span>
                    <div className="flex items-center gap-1">
                      {shortcut.keys.map((key, j) => (
                        <kbd key={j} className="px-2 py-1 rounded bg-white/10 text-gray-300 text-xs font-mono">
                          {key}
                        </kbd>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 border-t border-white/[0.06] bg-white/[0.02]">
              <p className="text-xs text-gray-500 text-center">
                Still stuck? Click <span className="text-teal-300 font-medium">Share feedback</span> in the header or email <a href="mailto:support@voxinity.ai" className="text-teal-300 hover:underline">support@voxinity.ai</a>
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Workspace Switcher Component
const WorkspaceSwitcher = ({ collapsed }) => {
  const { api, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWorkspaces();
  }, []);

  const fetchWorkspaces = async () => {
    try {
      const response = await api.get('/api/workspaces');
      setWorkspaces(response.data);
      
      const savedWorkspaceId = localStorage.getItem('currentWorkspaceId');
      const currentWs = response.data.find(ws => ws.workspace_id === savedWorkspaceId) || 
                       response.data.find(ws => ws.is_primary) ||
                       response.data[0];
      
      if (currentWs) {
        setCurrentWorkspace(currentWs);
        localStorage.setItem('currentWorkspaceId', currentWs.workspace_id);
      }
    } catch (error) {
      console.error('Failed to fetch workspaces:', error);
    } finally {
      setLoading(false);
    }
  };

  const switchWorkspace = (workspace) => {
    setCurrentWorkspace(workspace);
    localStorage.setItem('currentWorkspaceId', workspace.workspace_id);
    setIsOpen(false);
    toast.success(`Switched to ${workspace.name}`);
    navigate(`/workspace/${workspace.workspace_id}`);
  };

  const filteredWorkspaces = workspaces.filter(ws => 
    ws.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className={`p-3 ${collapsed ? 'px-2' : ''}`}>
        <div className="animate-pulse bg-white/10 rounded-lg h-12"></div>
      </div>
    );
  }

  if (collapsed) {
    return (
      <div className="p-2">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full aspect-square rounded-lg bg-gradient-to-br from-teal-400/20 to-violet-400/20 flex items-center justify-center hover:from-teal-400/30 hover:to-violet-400/30 transition-colors relative"
          title={currentWorkspace?.name || 'Select Workspace'}
        >
          <span 
            className="text-white font-bold text-sm"
            style={{ color: currentWorkspace?.color || '#14b8a6' }}
          >
            {currentWorkspace?.name?.charAt(0) || 'W'}
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className="p-3 relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors text-left"
        data-testid="workspace-switcher"
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
            style={{ backgroundColor: currentWorkspace?.color || '#14b8a6' }}
          >
            {(currentWorkspace?.business_name || currentWorkspace?.name)?.charAt(0) || 'W'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {currentWorkspace?.business_name || currentWorkspace?.name || 'Select Workspace'}
            </p>
            <div className="flex items-center gap-1.5">
              <p className="text-xs text-gray-400 uppercase">
                {(currentWorkspace?.is_lifetime ? 'Pro plan' : (!currentWorkspace?.plan || currentWorkspace?.plan === 'launchpad') ? 'Pay as you go' : `${currentWorkspace.plan} Plan`)}
              </p>
              {currentWorkspace?.is_lifetime && (
                <span className="px-1.5 py-0 rounded text-[9px] font-bold bg-gradient-to-r from-amber-500/25 to-yellow-400/25 text-amber-300 border border-amber-400/30 tracking-wider" data-testid="workspace-switcher-lifetime-badge">
                  LIFETIME
                </span>
              )}
              {currentWorkspace?.is_beta && !currentWorkspace?.is_lifetime && (
                <span
                  className="px-1.5 py-0 rounded text-[9px] font-extrabold border tracking-wider"
                  style={{ background: 'linear-gradient(135deg,#cbd5e1,#94a3b8)', color: '#0a0b0f', borderColor: 'rgba(148,163,184,0.5)' }}
                  data-testid="workspace-switcher-beta-badge"
                >
                  BETA
                </span>
              )}
            </div>
          </div>
          <ChevronDown size={16} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute left-3 right-3 top-full mt-2 bg-space-800 border border-white/10 rounded-xl shadow-xl z-50 overflow-hidden"
            >
              <div className="p-2 border-b border-white/10">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search workspaces..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-teal-400/50"
                  />
                </div>
              </div>

              <div className="max-h-64 overflow-y-auto p-2">
                {filteredWorkspaces.map((workspace) => {
                  const displayName = workspace.business_name || workspace.name;
                  return (
                    <button
                      key={workspace.workspace_id}
                      onClick={() => switchWorkspace(workspace)}
                      className={`w-full p-2 rounded-lg flex items-center gap-3 transition-colors ${
                        currentWorkspace?.workspace_id === workspace.workspace_id
                          ? 'bg-teal-400/10 text-teal-400'
                          : 'hover:bg-white/5 text-white'
                      }`}
                    >
                      <div 
                        className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold"
                        style={{ backgroundColor: workspace.color || '#14b8a6' }}
                      >
                        {displayName.charAt(0)}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-medium truncate">{displayName}</p>
                          {workspace.is_beta && !workspace.is_lifetime && (
                            <span className="px-1 rounded text-[8px] font-bold tracking-wider flex-shrink-0 border"
                                  style={{ background: 'linear-gradient(135deg,#cbd5e1,#94a3b8)', color: '#0a0b0f', borderColor: 'rgba(148,163,184,0.5)' }}
                                  data-testid={`workspace-beta-badge-${workspace.workspace_id}`}>
                              BETA
                            </span>
                          )}
                          {workspace.is_lifetime && (
                            <span className="px-1 rounded text-[8px] font-bold bg-amber-400/20 text-amber-300 border border-amber-400/30 tracking-wider flex-shrink-0">
                              LIFETIME
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400">{workspace.is_lifetime ? 'Lifetime' : (!workspace.plan || workspace.plan === 'launchpad') ? 'Pay as you go' : workspace.plan} · {workspace.permission_level}</p>
                      </div>
                      {currentWorkspace?.workspace_id === workspace.workspace_id && (
                        <Check size={14} className="text-teal-400" />
                      )}
                    </button>
                  );
                })}
              </div>

              {isAdmin && (
                <div className="p-2 border-t border-white/10">
                  <Link
                    to="/admin"
                    onClick={() => setIsOpen(false)}
                    className="w-full p-2 rounded-lg flex items-center gap-2 text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
                  >
                    <Plus size={14} />
                    <span className="text-sm">Manage All Workspaces</span>
                  </Link>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Lead scan progress widget (sidebar, above Balance) ──────────────
// Shows live % + ETA while a market scan runs; flips to a "complete"
// state that stays until the user opens Leads & Outreach.
const readScanLS = (k) => { try { return JSON.parse(localStorage.getItem(k) || '[]'); } catch { return []; } };
const writeScanLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v.slice(-30))); } catch { /* ignore */ } };

const LeadScanWidget = ({ api, workspaceId, isDarkMode, collapsed }) => {
  const location = useLocation();
  const [job, setJob] = useState(null);
  const [mode, setMode] = useState(null); // running | done | error
  const pollRef = useRef(null);

  const pick = useCallback((jobs) => {
    const scans = (jobs || []).filter(j => j.type === 'search');
    const running = scans.find(j => j.status === 'running');
    if (running) {
      const watched = readScanLS('vx_scan_watched');
      if (!watched.includes(running.job_id)) writeScanLS('vx_scan_watched', [...watched, running.job_id]);
      setJob(running); setMode('running');
      return true;
    }
    const watched = readScanLS('vx_scan_watched');
    const dismissed = readScanLS('vx_scan_dismissed');
    const finished = scans.find(j => watched.includes(j.job_id) && !dismissed.includes(j.job_id)
      && (j.status === 'done' || j.status === 'error'));
    if (finished) {
      setJob(finished); setMode(finished.status === 'done' ? 'done' : 'error');
    } else {
      setJob(null); setMode(null);
    }
    return false;
  }, []);

  const fetchJobs = useCallback(async () => {
    if (!workspaceId) return false;
    try {
      const r = await api.get(`/api/workspaces/${workspaceId}/leads/jobs`);
      return pick(r.data.jobs || []);
    } catch { return false; }
  }, [api, workspaceId, pick]);

  useEffect(() => {
    let alive = true;
    const loop = async () => {
      const running = await fetchJobs();
      if (!alive) return;
      clearTimeout(pollRef.current);
      if (running) pollRef.current = setTimeout(loop, 5000);
    };
    loop();
    const onStart = () => loop();
    window.addEventListener('voxinity:lead-scan-started', onStart);
    return () => {
      alive = false;
      clearTimeout(pollRef.current);
      window.removeEventListener('voxinity:lead-scan-started', onStart);
    };
  }, [fetchJobs]);

  // Dismiss the completed card once the user opens Leads & Outreach
  useEffect(() => {
    if ((mode === 'done' || mode === 'error') && job && location.pathname.includes('/leads')) {
      writeScanLS('vx_scan_dismissed', [...readScanLS('vx_scan_dismissed'), job.job_id]);
      setJob(null); setMode(null);
    }
  }, [location.pathname, mode, job]);

  if (!job) return null;
  const p = job.progress || {};
  const pct = Math.min(100, p.pct || 0);
  const eta = p.eta_seconds || 0;
  const etaTxt = mode === 'running' && eta > 0
    ? (eta < 75 ? `~${eta}s left` : `~${Math.round(eta / 60)}m left`) : '';
  const city = job.params?.city || '';

  if (collapsed) {
    return (
      <div className="px-1 py-1 flex justify-center" data-testid="lead-scan-widget-mini">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${
          mode === 'running' ? 'border-teal-400/40 bg-teal-400/10'
            : mode === 'done' ? 'border-emerald-400/40 bg-emerald-400/10'
              : 'border-red-400/40 bg-red-400/10'}`}
          title={mode === 'running' ? `Scanning ${city} — ${pct}%${etaTxt ? ` · ${etaTxt}` : ''}`
            : mode === 'done' ? `Scan complete — ${p.new || 0} new leads` : 'Scan failed'}>
          {mode === 'running' ? <Radar size={16} className="text-teal-400 animate-pulse" />
            : mode === 'done' ? <CheckCircle2 size={16} className="text-emerald-400" />
              : <XCircle size={16} className="text-red-400" />}
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 pb-1" data-testid="lead-scan-widget">
      <div className={`rounded-xl border p-3 ${
        mode === 'running'
          ? isDarkMode ? 'border-teal-400/25 bg-gradient-to-br from-teal-400/[0.07] to-violet-500/[0.07]' : 'border-teal-300 bg-teal-50'
          : mode === 'done'
            ? isDarkMode ? 'border-emerald-400/30 bg-emerald-400/[0.07]' : 'border-emerald-300 bg-emerald-50'
            : isDarkMode ? 'border-red-400/30 bg-red-400/[0.07]' : 'border-red-300 bg-red-50'}`}>
        <div className="flex items-center gap-2">
          {mode === 'running' ? (
            <span className="relative flex w-6 h-6 items-center justify-center shrink-0">
              <span className="absolute inline-flex h-full w-full rounded-full bg-teal-400/30 animate-ping" style={{ animationDuration: '1.6s' }} />
              <Radar size={14} className="relative text-teal-400" />
            </span>
          ) : mode === 'done' ? (
            <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />
          ) : (
            <XCircle size={16} className="text-red-400 shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <p className={`text-[11px] font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`} data-testid="lead-scan-widget-title">
              {mode === 'running' ? `Scanning ${city}…` : mode === 'done' ? 'Scan complete' : 'Scan failed'}
            </p>
            <p className={`text-[10px] truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} data-testid="lead-scan-widget-sub">
              {mode === 'running'
                ? `${pct}%${etaTxt ? ` · ${etaTxt}` : ''} · ${p.found || 0} found`
                : mode === 'done'
                  ? `${p.new || 0} new leads ready`
                  : (job.error || 'Something went wrong')}
            </p>
          </div>
        </div>
        {mode === 'running' && (
          <div className={`h-1.5 mt-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`}>
            <div className="h-full rounded-full bg-gradient-to-r from-teal-400 to-violet-400 transition-all duration-700"
              style={{ width: `${Math.max(3, pct)}%` }} data-testid="lead-scan-widget-bar" />
          </div>
        )}
      </div>
    </div>
  );
};

// Main Dashboard Layout Component
const DashboardLayout = ({ children }) => {
  const { user, logout, isAdmin, api } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const betaEnd = useBetaEnd();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebarCollapsed');
      return saved ? JSON.parse(saved) : false;
    } catch { return false; }
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  const [isUnlimited, setIsUnlimited] = useState(false);

  // ── Beta countdown timer + lifetime-offer popup ────────────────────
  // Read directly from BetaEndContext (already polls /api/me/beta-status
  // every 60s, so we get fresh state automatically). The countdown ticks
  // locally via `useCountdown` for sub-second smoothness.
  const programEndsAt = betaEnd?.status?.program_ends_at || null;
  const showCountdownBadge = !!(
    betaEnd?.status?.is_beta &&
    !betaEnd?.status?.is_lifetime &&
    !betaEnd?.status?.program_ended &&
    programEndsAt
  );
  const countdown = useCountdown(showCountdownBadge ? programEndsAt : null);

  // Auto-pop the lifetime offer modal — driven by server state
  // (`show_lifetime_popup` is true when the user hasn't seen the modal
  // in the last 24h AND a countdown is active). Manual-click also opens.
  const [lifetimeModalOpen, setLifetimeModalOpen] = useState(false);
  const [lifetimeModalIsManual, setLifetimeModalIsManual] = useState(false);
  const autoPopFiredRef = React.useRef(false);
  useEffect(() => {
    if (!betaEnd?.status?.show_lifetime_popup) return;
    // Defensive guard — never auto-pop in a workspace that's already
    // on Lifetime. (Backend gates this too, but defense in depth: the
    // popup is offering the lifetime plan; surfacing it to someone who
    // already owns lifetime is redundant and confusing.) Also closes
    // the modal if the workspace flips to lifetime while it's open.
    if (betaEnd?.status?.is_lifetime) {
      if (lifetimeModalOpen) setLifetimeModalOpen(false);
      return;
    }
    if (autoPopFiredRef.current) return;  // Only auto-fire once per page load
    autoPopFiredRef.current = true;
    // Tiny delay so the rest of the dashboard renders first.
    const t = setTimeout(() => {
      setLifetimeModalIsManual(false);
      setLifetimeModalOpen(true);
    }, 1200);
    return () => clearTimeout(t);
  }, [betaEnd?.status?.show_lifetime_popup, betaEnd?.status?.is_lifetime, lifetimeModalOpen]);
  const [isLifetime, setIsLifetime] = useState(false);
  const [isBeta, setIsBeta] = useState(false);
  const [lowCreditSubAccounts, setLowCreditSubAccounts] = useState([]);
  const [creditAlertShown, setCreditAlertShown] = useState(false);
  const [alertsDismissed, setAlertsDismissed] = useState(false);
  const [canSeeAffiliates, setCanSeeAffiliates] = useState(false);
  const commandPalette = useCommandPalette();
  const currentWorkspaceId = localStorage.getItem('currentWorkspaceId');
  
  // Branding state
  const [branding, setBranding] = useState({
    business_name: null,
    logo_data: null,
    whitelabel_enabled: false
  });

  // Fetch branding when workspace changes
  useEffect(() => {
    if (currentWorkspaceId) {
      fetchBranding();
      fetchCreditBalance();
      setAlertsDismissed(false);
      setCreditAlertShown(false);
      fetchCreditHealth();
      const interval = setInterval(() => {
        fetchCreditBalance();
        fetchCreditHealth();
      }, 120000);

      // Listen for explicit refresh triggers (e.g., after a successful top-up)
      const onBalanceUpdated = () => { fetchCreditBalance(); fetchCreditHealth(); };
      window.addEventListener('voxinity:balance-updated', onBalanceUpdated);

      return () => {
        clearInterval(interval);
        window.removeEventListener('voxinity:balance-updated', onBalanceUpdated);
      };
    }
  }, [currentWorkspaceId]);

  // Dismiss alerts when user navigates to sub-accounts page
  useEffect(() => {
    if (location.pathname.includes('/sub-accounts') && !location.pathname.includes('/sub-account/')) {
      setAlertsDismissed(true);
    }
  }, [location.pathname]);

  const fetchCreditBalance = async () => {
    try {
      const resp = await api.get(`/api/workspaces/${currentWorkspaceId}/credits/balance`);
      setCreditBalance(resp.data?.balance ?? resp.data?.credits ?? null);
      setIsUnlimited(resp.data?.unlimited ?? false);
      setIsLifetime(resp.data?.is_lifetime ?? false);
      setIsBeta(resp.data?.is_beta ?? false);
    } catch (err) {
      // Silently fail
    }
  };

  const fetchCreditHealth = async () => {
    try {
      const resp = await api.get(`/api/workspaces/${currentWorkspaceId}/sub-accounts/credit-health`);
      const alerts = resp.data?.alerts || [];
      setLowCreditSubAccounts(alerts);
    } catch (err) {
      // Silently fail
    }
  };

  // Affiliates is visible to every workspace member regardless of role
  // — agencies want every team-mate to be able to share their affiliate
  // link / track signups, not just owners/admins. Backend still gates
  // sensitive writes (payouts, commission edits) by role; this just
  // controls sidebar visibility.
  useEffect(() => {
    if (!user) return;
    if (isAdmin) { setCanSeeAffiliates(true); return; }
    if (!currentWorkspaceId) {
      setCanSeeAffiliates(false);
      return;
    }
    let cancelled = false;
    api.get('/api/workspaces')
      .then((resp) => {
        if (cancelled) return;
        // Show the link to anyone with access to this workspace —
        // permission_level just needs to exist (member, admin, owner, viewer).
        const ws = (resp.data || []).find((w) => w.workspace_id === currentWorkspaceId);
        setCanSeeAffiliates(!!ws);
      })
      .catch(() => { if (!cancelled) setCanSeeAffiliates(false); });
    return () => { cancelled = true; };
  }, [currentWorkspaceId, isAdmin, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBranding = async () => {
    try {
      const response = await api.get(`/api/workspaces/${currentWorkspaceId}/branding`);
      setBranding(response.data);
    } catch (error) {
      console.error('Failed to fetch branding:', error);
    }
  };

  const toggleCollapse = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newCollapsed));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Agency/Workspace menu items (removed Phone Numbers and Usage Reports)
  const workspaceMenuItems = [
    { label: 'Dashboard', icon: LayoutDashboard, href: `/workspace/${currentWorkspaceId}` },
    { label: 'Sub-Accounts', icon: Users, href: `/workspace/${currentWorkspaceId}/sub-accounts` },
    { label: 'Leads & Outreach', icon: Radar, href: `/workspace/${currentWorkspaceId}/leads` },
    { label: 'Onboarding', icon: ClipboardList, href: `/workspace/${currentWorkspaceId}/onboarding` },
    { label: 'Integrations', icon: Link2, href: `/workspace/${currentWorkspaceId}/integrations` },
    { label: 'Billing', icon: CreditCard, href: `/workspace/${currentWorkspaceId}/billing` },
    { label: 'Settings', icon: Settings, href: '/settings' },
    { label: 'Training', icon: GraduationCap, href: `/workspace/${currentWorkspaceId}/training` },
    // Affiliates hidden from the menu for now (route still exists).
  ];

  const isActiveRoute = (href) => {
    if (href === `/workspace/${currentWorkspaceId}`) {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  };

  // Theme-aware classes
  const bgClass = isDarkMode ? 'bg-space-900' : 'bg-gray-50';
  const sidebarBgClass = isDarkMode ? 'bg-space-800/50' : 'bg-white';
  const borderClass = isDarkMode ? 'border-white/5' : 'border-gray-200';
  const textClass = isDarkMode ? 'text-white' : 'text-gray-900';
  const mutedTextClass = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const hoverBgClass = isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-100';

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed, currentWorkspaceId }}>
      <div className={`min-h-screen ${bgClass} flex`}>
        {mobileNavOpen && <button className="fixed inset-0 z-40 bg-black/60 md:hidden" onClick={() => setMobileNavOpen(false)} aria-label="Close navigation" data-testid="workspace-mobile-nav-backdrop"/>}
        {/* Sidebar */}
        <aside 
          data-testid="workspace-sidebar"
          onClick={e => { if (e.target.closest('a')) setMobileNavOpen(false); }}
          className={`fixed left-0 top-0 h-full ${sidebarBgClass} border-r ${borderClass} flex flex-col z-50 transition-[transform,width] duration-300 ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 ${
            collapsed ? 'w-16' : 'w-64'
          }`}
        >
          {/* Logo */}
          <div className={`p-4 border-b ${borderClass} flex items-center ${collapsed || (branding.whitelabel_enabled && branding.logo_data) ? 'justify-center' : 'gap-2'}`}>
            <Link to="/" className="flex items-center justify-center gap-2">
              {branding.whitelabel_enabled && branding.logo_data ? (
                <LogoImage logoData={branding.logo_data} businessName={branding.business_name} collapsed={collapsed} />
              ) : (
                <img 
                  src={collapsed 
                    ? (isDarkMode ? '/voxinity-icon.png?v=8' : '/voxinity-icon-light.png?v=8')
                    : (isDarkMode ? '/voxinity-logo.png?v=8' : '/voxinity-logo-light.png?v=8')
                  } 
                  alt="Voxinity AI" 
                  className={collapsed ? 'h-7 w-auto max-w-[32px] object-contain' : 'h-9 w-auto'} 
                />
              )}
            </Link>
          </div>

          {/* Workspace Switcher */}
          <WorkspaceSwitcher collapsed={collapsed} />

          {/* Navigation */}
          <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
            {!collapsed && (
              <div className="px-3 py-2">
                <span className={`text-xs font-medium ${mutedTextClass} uppercase tracking-wider`}>Agency Mode</span>
              </div>
            )}

            {workspaceMenuItems.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                  isActiveRoute(item.href)
                    ? 'bg-teal-400/10 text-teal-400'
                    : `${mutedTextClass} ${hoverBgClass} hover:${textClass}`
                } ${collapsed ? 'justify-center' : ''}`}
                title={collapsed ? item.label : undefined}
              >
                <div className="relative flex-shrink-0">
                  <item.icon size={20} />
                  {item.label === 'Sub-Accounts' && lowCreditSubAccounts.length > 0 && !alertsDismissed && (
                    <span className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full ${
                      lowCreditSubAccounts.some(a => a.status === 'zero') ? 'bg-red-500' : 'bg-amber-400'
                    } ring-2 ${isDarkMode ? 'ring-space-800/50' : 'ring-white'}`} />
                  )}
                </div>
                {!collapsed && (
                  <span className="font-medium text-sm flex-1">{item.label}</span>
                )}
                {!collapsed && item.label === 'Sub-Accounts' && lowCreditSubAccounts.length > 0 && !alertsDismissed && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                    lowCreditSubAccounts.some(a => a.status === 'zero')
                      ? 'bg-red-500/10 text-red-400'
                      : 'bg-amber-400/10 text-amber-400'
                  }`}>
                    {lowCreditSubAccounts.length}
                  </span>
                )}
              </Link>
            ))}

            {/* Admin Section - Only for users with admin access */}
            {isAdmin && (
              <>
                {!collapsed && (
                  <div className="px-3 py-2 mt-4">
                    <span className={`text-xs font-medium ${mutedTextClass} uppercase tracking-wider`}>Admin</span>
                  </div>
                )}
                <Link
                  to="/admin"
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                    location.pathname === '/admin'
                      ? 'bg-violet-400/10 text-violet-400'
                      : `${mutedTextClass} hover:bg-violet-400/10 hover:text-violet-400`
                  } ${collapsed ? 'justify-center' : ''}`}
                  title={collapsed ? 'Admin Panel' : undefined}
                >
                  <Shield size={20} className="flex-shrink-0" />
                  {!collapsed && <span className="font-medium text-sm">Admin Panel</span>}
                </Link>
                <Link
                  to="/admin/funnel"
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                    location.pathname === '/admin/funnel'
                      ? 'bg-violet-400/10 text-violet-400'
                      : `${mutedTextClass} hover:bg-violet-400/10 hover:text-violet-400`
                  } ${collapsed ? 'justify-center' : ''}`}
                  title={collapsed ? 'Challenge Funnel' : undefined}
                  data-testid="nav-funnel-admin"
                >
                  <Flame size={20} className="flex-shrink-0" />
                  {!collapsed && <span className="font-medium text-sm">Challenge Funnel</span>}
                </Link>
              </>
            )}
          </nav>

          {/* Collapse Toggle */}
          <button
            onClick={toggleCollapse}
            className={`absolute -right-3 top-20 w-6 h-6 rounded-full ${isDarkMode ? 'bg-space-800' : 'bg-white'} border ${borderClass} flex items-center justify-center ${mutedTextClass} hover:${textClass} transition-colors`}
            data-testid="sidebar-collapse-btn"
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>

          {/* Beta-ended banner OR Quick Start — banner takes priority post-beta-end */}
          <div className={`${collapsed ? 'px-1 py-1 flex justify-center' : 'px-3 py-1'}`}>
            {betaEnd?.status?.show_end_modal && betaEnd?.dismissed ? (
              <BetaEndedBanner collapsed={collapsed} />
            ) : (
              <QuickStartChecklist collapsed={collapsed} isDarkMode={isDarkMode} />
            )}
          </div>

          {/* Lead scan progress — shows above Balance while a market scan runs */}
          <LeadScanWidget api={api} workspaceId={currentWorkspaceId} isDarkMode={isDarkMode} collapsed={collapsed} />
          <LeadRunNotice api={api} workspaceId={currentWorkspaceId} isDarkMode={isDarkMode} collapsed={collapsed} />

          {/* Credit Balance */}
          <div className={`py-2 border-t ${borderClass} ${collapsed ? 'px-1 flex justify-center' : 'px-3'}`}>
            <button
              onClick={() => !isUnlimited && navigate(`/workspace/${currentWorkspaceId}/billing`)}
              className={`${collapsed ? 'w-10 h-10 flex items-center justify-center rounded-xl' : 'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl'} transition-all ${
                isUnlimited
                  ? isDarkMode ? 'bg-gradient-to-r from-amber-400/[0.06] to-orange-400/[0.06] border border-amber-400/20' : 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200'
                  : isDarkMode ? 'bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08]' : 'bg-gray-100 hover:bg-gray-200 border border-gray-200'
              }`}
              data-testid="agency-credit-balance-btn"
              style={isUnlimited ? { cursor: 'default' } : {}}
              title={collapsed ? (isUnlimited ? 'Balance: Unlimited' : `Balance: $${creditBalance ?? '---'}`) : undefined}
            >
              <div className={`${collapsed ? '' : 'w-7 h-7 rounded-lg'} flex items-center justify-center shrink-0 ${collapsed ? '' : isUnlimited ? 'bg-gradient-to-br from-amber-400/30 to-orange-500/30' : 'bg-gradient-to-br from-amber-400/20 to-orange-500/20'}`}>
                {isUnlimited ? <Infinity size={collapsed ? 18 : 14} className="text-amber-400" /> : <Coins size={collapsed ? 18 : 14} className="text-amber-400" />}
              </div>
              {!collapsed && (
                <div className="text-left min-w-0 flex-1">
                  <p className={`text-[10px] uppercase tracking-wider ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Balance</p>
                  {isUnlimited ? (
                    <div className="flex items-center gap-1.5">
                      <Infinity size={18} className={`${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                      <span className={`text-xs font-medium ${isDarkMode ? 'text-amber-400/70' : 'text-amber-600/70'}`}>Unlimited</span>
                    </div>
                  ) : (
                    <p className={`text-sm font-bold tabular-nums ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                      ${creditBalance !== null ? Number(creditBalance).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '---'}
                    </p>
                  )}
                </div>
              )}
              {!collapsed && !isUnlimited && (
                <div className="flex items-center">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-400/10 text-amber-400 font-medium">+</span>
                </div>
              )}
            </button>
          </div>

          {/* User Profile */}
          <div className={`border-t ${borderClass} ${collapsed ? 'p-1 flex justify-center' : 'p-3'}`}>
            <div className={`flex items-center ${collapsed ? 'justify-center p-1.5' : 'gap-3 p-2'} rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-100'}`}>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-violet-400 flex items-center justify-center flex-shrink-0">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                ) : (
                  <span className="text-white text-xs font-medium">{user?.name?.charAt(0) || 'U'}</span>
                )}
              </div>
              {!collapsed && (
                <>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${textClass} truncate`}>{user?.name}</p>
                    <p className={`text-xs ${mutedTextClass} truncate`}>{user?.permission_level || 'viewer'}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className={`p-1.5 rounded-lg ${mutedTextClass} hover:text-red-400 hover:bg-red-400/10 transition-colors`}
                    data-testid="sidebar-logout-btn"
                  >
                    <LogOut size={16} />
                  </button>
                </>
              )}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className={`flex-1 min-w-0 min-h-screen transition-[margin] duration-300 ml-0 ${collapsed ? 'md:ml-16' : 'md:ml-64'}`}>
          {/* Top Bar */}
          <div className={`sticky top-0 z-40 ${isDarkMode ? 'bg-space-900/80' : 'bg-white/80'} backdrop-blur-xl border-b ${borderClass}`}>
            <div className="flex items-center justify-end gap-2 px-4 sm:px-6 py-3">
              <button className={`mr-auto md:hidden p-2 rounded-md ${textClass}`} aria-label="Open navigation" aria-expanded={mobileNavOpen} data-testid="workspace-mobile-nav-toggle" onClick={() => { setCollapsed(false); setMobileNavOpen(true); }}><Menu size={20}/></button>
              {/* Join the Community — leftmost in the top-right cluster */}
              <a
                href="https://discord.gg/cUgxTYnpM"
                target="_blank"
                rel="noopener noreferrer"
                className={`group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-all text-[12px] font-medium ${
                  isDarkMode
                    ? 'border-violet-400/30 bg-gradient-to-r from-violet-500/[0.08] via-teal-400/[0.06] to-violet-500/[0.08] text-violet-100 hover:from-violet-500/[0.16] hover:via-teal-400/[0.12] hover:to-violet-500/[0.16] hover:border-violet-400/55 hover:shadow-[0_0_18px_-6px_rgba(167,139,250,0.6)]'
                    : 'border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100 hover:border-violet-400'
                }`}
                data-testid="header-community-btn"
                title="Join the Voxinity AI community on Discord"
              >
                <Users size={13} className={`transition-colors ${isDarkMode ? 'text-violet-300 group-hover:text-violet-200' : 'text-violet-600'}`} />
                <span className="hidden sm:inline">Community</span>
              </a>

              {/* ── Beta countdown badge ──────────────────────────────
                  Live countdown to beta-end. Click → opens the
                  LifetimeOfferModal manually (no 24h cooldown applies
                  to a manual open — only the auto-popup is rate-limited). */}
              {showCountdownBadge && !countdown.expired && (
                <button
                  onClick={() => {
                    setLifetimeModalIsManual(true);
                    setLifetimeModalOpen(true);
                  }}
                  className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-rose-400/50 bg-gradient-to-r from-rose-500/[0.12] via-orange-500/[0.08] to-rose-500/[0.12] text-rose-100 hover:from-rose-500/25 hover:via-orange-500/20 hover:to-rose-500/25 hover:border-rose-400/70 hover:shadow-[0_0_24px_-4px_rgba(244,63,94,0.7)] transition-all text-[12px] font-bold tracking-wide relative overflow-hidden"
                  data-testid="header-beta-countdown-badge"
                  title="Click to lock in lifetime at $3,000 before the timer ends"
                >
                  {/* Pulsing accent — urgency */}
                  <span className="absolute inset-0 bg-gradient-to-r from-rose-500/0 via-rose-500/[0.15] to-rose-500/0 opacity-0 group-hover:opacity-100 animate-pulse pointer-events-none" aria-hidden />
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-rose-400" />
                  </span>
                  <span className="relative hidden md:inline text-[10px] uppercase tracking-[0.16em] text-rose-300/90">Beta Ends</span>
                  <span className="relative tabular-nums text-white" data-testid="header-beta-countdown-value">
                    {countdown.d > 0 ? `${countdown.d}d ` : ''}
                    {String(countdown.h).padStart(2, '0')}h{' '}
                    {String(countdown.m).padStart(2, '0')}m{' '}
                    <span className="text-rose-200">{String(countdown.s).padStart(2, '0')}s</span>
                  </span>
                </button>
              )}

              {/* Lifetime Badge */}
              {isLifetime && (
                <div
                  className="relative group"
                  data-testid="lifetime-badge"
                >
                  <div className={`px-3 py-1.5 rounded-lg border flex items-center gap-1.5 cursor-default ${
                    isDarkMode
                      ? 'bg-gradient-to-r from-amber-500/20 via-yellow-400/20 to-amber-500/20 border-amber-400/30'
                      : 'bg-gradient-to-r from-amber-100 via-yellow-50 to-amber-100 border-amber-300'}`}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className={isDarkMode ? 'text-amber-400' : 'text-amber-600'}>
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor" stroke="currentColor" strokeWidth="1" />
                    </svg>
                    <span className={`text-[11px] font-bold tracking-widest uppercase ${
                      isDarkMode
                        ? 'bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-300 bg-clip-text text-transparent'
                        : 'text-amber-700'}`}>
                      Lifetime
                    </span>
                  </div>
                  <div className="absolute top-full right-0 mt-1 px-2.5 py-1.5 rounded-lg bg-black/90 text-[10px] text-amber-200/80 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none border border-amber-500/10">
                    Lifetime Pro Access — No monthly fees
                  </div>
                </div>
              )}

              {/* Beta Badge */}
              {isBeta && !isLifetime && (
                <div className="relative group" data-testid="beta-badge">
                  <div
                    className="px-3 py-1.5 rounded-lg border flex items-center gap-1.5 cursor-default"
                    style={{
                      background: 'linear-gradient(135deg, rgba(203,213,225,0.2) 0%, rgba(148,163,184,0.25) 50%, rgba(203,213,225,0.2) 100%)',
                      borderColor: 'rgba(148,163,184,0.45)'
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-slate-200">
                      <path d="M12 2l1.8 5.6H20l-4.9 3.6L17 17l-5-3.6L7 17l1.9-5.8L4 7.6h6.2L12 2z" fill="currentColor" />
                    </svg>
                    <span
                      className="text-[11px] font-extrabold tracking-widest uppercase bg-gradient-to-r from-slate-100 via-white to-slate-100 bg-clip-text text-transparent"
                    >
                      Beta Member
                    </span>
                  </div>
                  <div className="absolute top-full right-0 mt-1 px-2.5 py-1.5 rounded-lg bg-black/90 text-[10px] text-slate-200/80 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none border border-white/10">
                    Voxinity AI Beta Member — VIP access
                  </div>
                </div>
              )}

              {/* Share Feedback — sits next to Beta Member badge */}
              {isBeta && !isLifetime && (
                <button
                  onClick={() => { if (typeof window !== 'undefined') window.dispatchEvent(new Event('open-feedback-modal')); }}
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-teal-400/30 bg-teal-400/[0.06] text-teal-200 hover:bg-teal-400/[0.12] hover:border-teal-400/50 transition-colors text-[12px] font-medium"
                  data-testid="header-feedback-btn"
                  title="Share feedback with the team"
                >
                  <MessageCircle size={13} />
                  <span>Share feedback</span>
                </button>
              )}

              {/* In-app notification bell — workspace-scoped when we're
                  inside a workspace shell. Glows + pulses when there are
                  unread alerts (BYOK key invalid, low balance, etc.). */}
              <NotificationBell
                isDarkMode={isDarkMode}
                workspaceId={currentWorkspaceId}
                mutedTextClass={mutedTextClass}
                hoverBgClass={hoverBgClass}
              />

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg ${mutedTextClass} ${hoverBgClass} transition-colors`}
                data-testid="theme-toggle-btn"
                title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
              </button>

              {/* Help Button */}
              <button
                onClick={() => setShowHelp(true)}
                className={`p-2 rounded-lg ${mutedTextClass} ${hoverBgClass} transition-colors`}
                data-testid="help-btn"
                title="Help & Shortcuts"
              >
                <HelpCircle size={18} />
              </button>
            </div>
          </div>

          {/* Payment Failed / Grace Period Alert */}
          {currentWorkspaceId && (
            <PaymentAlertBanner workspaceId={currentWorkspaceId} />
          )}

          {/* Out-of-slots upgrade popup — pops when an onboarding
              submission couldn't auto-create a sub-account. */}
          {currentWorkspaceId && (
            <CapacityUpgradeModal workspaceId={currentWorkspaceId} />
          )}

          {/* Beta Ended — "Choose a plan / Lifetime Deal" top banner.
              Takes priority over the paused-subaccounts banner since the cause
              is beta ending, not literal balance depletion. */}
          {betaEnd?.status?.show_end_modal && !betaEnd?.status?.is_lifetime && !location.pathname.includes('/billing') && (
            <div className="px-6 pt-4" data-testid="beta-ended-top-banner">
              <div className="relative rounded-xl px-4 py-3 border-2 flex items-center gap-3 overflow-hidden"
                   style={{
                     background: 'linear-gradient(90deg, rgba(251,191,36,0.16), rgba(245,158,11,0.04))',
                     borderColor: 'rgba(251,191,36,0.5)',
                     boxShadow: '0 0 40px -15px rgba(251,191,36,0.4)',
                   }}>
                <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl opacity-30 pointer-events-none"
                     style={{ background: 'radial-gradient(circle, #fbbf24 0%, transparent 70%)' }} />
                <div className="relative w-8 h-8 rounded-lg bg-amber-400/20 flex items-center justify-center flex-shrink-0 animate-pulse">
                  <Crown className="text-amber-300" size={15} />
                </div>
                <div className="relative flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-1.5 py-0.5 rounded-full text-[9px] font-extrabold tracking-[0.15em] bg-amber-400/30 text-amber-100 border border-amber-400/50">LAST CHANCE</span>
                    <div className="text-sm font-semibold text-white">
                      Beta has ended — workspace paused. Lock in <b className="text-amber-300">LIFETIME at {betaEnd?.status?.lifetime_price_label || '$3,000'}</b> before it jumps to <s className="opacity-70">$5,000</s>.
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => betaEnd?.setDismissed && betaEnd.setDismissed(false)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-400 hover:bg-amber-500 text-[#0a0b0f] transition-colors whitespace-nowrap"
                  data-testid="beta-ended-top-banner-cta"
                >
                  Choose a plan
                </button>
              </div>
            </div>
          )}

          {/* Paused sub-accounts banner — surfaces when any sub-account is
              balance-depleted. Suppressed during the beta-ended paused state
              since that's handled by the banner above. */}
          {!alertsDismissed
            && lowCreditSubAccounts.some(a => a.status === 'zero')
            && !location.pathname.includes('/billing')
            && !betaEnd?.status?.show_end_modal
            && (
            <div className="px-6 pt-4" data-testid="paused-subaccounts-banner">
              <div className="relative rounded-xl px-4 py-3 border flex items-center gap-3 overflow-hidden"
                   style={{
                     background: 'linear-gradient(90deg, rgba(244,63,94,0.12), rgba(244,63,94,0.04))',
                     borderColor: 'rgba(244,63,94,0.35)',
                   }}>
                <div className="w-8 h-8 rounded-lg bg-rose-400/15 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="text-rose-300" size={15} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white">
                    {lowCreditSubAccounts.filter(a => a.status === 'zero').length} sub-account{lowCreditSubAccounts.filter(a => a.status === 'zero').length > 1 ? 's' : ''} paused — balance depleted
                  </div>
                  <div className="text-xs text-rose-200/70">Top up your agency balance to resume usage.</div>
                </div>
                <button
                  onClick={() => navigate(`/workspace/${currentWorkspaceId}/billing`)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-400/20 hover:bg-rose-400/30 text-rose-100 border border-rose-400/40 transition-colors"
                  data-testid="paused-banner-topup-btn"
                >
                  Top Up Balance
                </button>
                <button
                  onClick={() => setAlertsDismissed(true)}
                  className="p-1.5 rounded-lg text-rose-200/70 hover:text-white hover:bg-white/5 transition-colors"
                  title="Dismiss"
                  data-testid="paused-banner-dismiss-btn"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          )}

          {children}
          <ConcurrencyWatcher />
        </main>

        {/* Command Palette */}
        <CommandPalette 
          isOpen={commandPalette.isOpen} 
          onClose={commandPalette.close} 
        />

        {/* Help Modal */}
        <HelpModal 
          isOpen={showHelp} 
          onClose={() => setShowHelp(false)} 
        />

        {/* Lifetime Offer Modal — auto-popped every 24h during beta
            countdown, or opened manually by clicking the header badge.
            In admin-preview mode we force `isManualOpen` so the modal's
            dismiss handler skips the per-user 24h cooldown stamp
            (admin's own cooldown shouldn't be polluted by previewing
            someone else's workspace). */}
        <LifetimeOfferModal
          open={lifetimeModalOpen}
          onClose={() => {
            setLifetimeModalOpen(false);
            // After auto-popup dismissal, refresh beta status so the
            // server-side 24h cooldown is reflected immediately.
            if (!lifetimeModalIsManual && !betaEnd?.status?.is_admin_preview) {
              setTimeout(() => betaEnd?.refresh?.(), 800);
            }
          }}
          programEndsAt={programEndsAt}
          isManualOpen={lifetimeModalIsManual || !!betaEnd?.status?.is_admin_preview}
        />
      </div>
    </SidebarContext.Provider>
  );
};

export default DashboardLayout;
DashboardLayout;
